"use client";

import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
  HStack,
  Icon,
  CloseButton,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { phoneNumberService } from "../../voice-services/phoneNumberService";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../contexts/AuthContext";

interface CreatePhoneNumberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreatePhoneNumberDialog: React.FC<
  CreatePhoneNumberDialogProps
> = ({ isOpen, onClose, onSuccess }) => {
  const { isAdmin, currentClientId, clients } = useAuth();

  const [isLoading, setIsLoading] = React.useState(false);
  const toast = useToast();

  const modalContentBg = "white";
  const labelColor = "gray.700";
  const optionalTextColor = "gray.500";
  const placeholderColor = "gray.600";
  const infoTextColor = "gray.600";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const areaCodeStr = formData.get("areaCode") as string;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id || null;
      const params: {
        area_code?: number;
        userId?: string | null;
        company_name?: string;
      } = {};
      if (areaCodeStr) {
        const areaCode = Number.parseInt(areaCodeStr, 10);
        if (isNaN(areaCode) || areaCode < 200 || areaCode > 999) {
          throw new Error("Area code must be a valid 3-digit number");
        }
        params.area_code = areaCode;
      }
      params.userId = userId;

      const currentClient = clients.find((c) => c.id === currentClientId);
      if (isAdmin && currentClient) {
        params.company_name = currentClient.company_name;
      }

      await phoneNumberService.create(params);

      toast({
        title: "Phone number created successfully",
        status: "success",
        duration: 3000,
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the phone number",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalContentBg} maxW="400px">
        <form onSubmit={handleSubmit}>
          <ModalHeader
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pb={4}
            color={labelColor}
          >
            Buy Phone Number
            <CloseButton onClick={onClose} />
          </ModalHeader>

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color={labelColor}
                >
                  Area Code
                  <Text
                    color={optionalTextColor}
                    fontSize="sm"
                    fontWeight="normal"
                  >
                    (Optional)
                  </Text>
                </FormLabel>
                <Input
                  name="areaCode"
                  placeholder="e.g. 650"
                  _placeholder={{ color: placeholderColor }}
                  borderColor={"gray.500"}
                />
              </FormControl>

              <HStack spacing={2} color={infoTextColor} fontSize="sm">
                <Icon as={InfoIcon} />
                <Text>This number incurs a monthly fee of $2.00.</Text>
              </HStack>
            </VStack>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button
              colorScheme={"white"}
              textColor={"gray.800"}
              variant="ghost"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
              type="submit"
              isLoading={isLoading}
            >
              Save
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
